import React, { useState } from "react";
import { ShieldCheck, AlertTriangle } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import VerificationReportModal from "./VerificationReportModal";

/**
 * VerificationBadge — Identity Card verification badge.
 *
 * Props:
 *  - status:    "verified" | "fraud" (anything else → not rendered)
 *  - size:      "sm" (default) | "xs"
 *  - profileId: if provided, the badge becomes clickable and opens the
 *               verification report modal explaining the classification.
 */
export default function VerificationBadge({ status, size = "sm", profileId }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  if (status !== "verified" && status !== "fraud") return null;

  const isVerified = status === "verified";
  const color = isVerified ? "#059669" : "#DC2626";
  const label = isVerified ? t("verif.verified") : t("verif.fraud");
  const Icon = isVerified ? ShieldCheck : AlertTriangle;

  const padding = size === "xs" ? "px-1.5 py-0.5" : "px-2 py-0.5";
  const textSize = size === "xs" ? "text-[9px]" : "text-[10px]";
  const iconSize = size === "xs" ? "w-2.5 h-2.5" : "w-3 h-3";

  const clickable = Boolean(profileId);

  const content = (
    <span
      className={`inline-flex items-center gap-1 ${padding} ${textSize} font-bold tracking-widest uppercase border rounded-full`}
      style={{ color, borderColor: `${color}60`, backgroundColor: `${color}12` }}
    >
      <Icon className={iconSize} style={{ color }} />
      {label}
    </span>
  );

  if (!clickable) {
    return (
      <span data-testid={`verif-badge-${status}`} title={label}>{content}</span>
    );
  }

  return (
    <>
      <button
        type="button"
        data-testid={`verif-badge-${status}`}
        title={t("verif.report.tooltip")}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="transition-transform hover:scale-105 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00E5FF]/40 rounded-full"
      >
        {content}
      </button>
      {open && (
        <VerificationReportModal
          profileId={profileId}
          status={status}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
